/**
 * Error Class Hierarchy
 *
 * Defines all error types used by the Local Workflow Engine.
 * Provides structured, predictable error handling across the system.
 *
 * @packageDocumentation
 */

/**
 * Base error class for all workflow-related errors.
 * All engine errors extend this class for consistent error handling.
 *
 * @remarks
 * This base class ensures:
 * - Proper error names for stack traces
 * - Consistent error structure
 * - Type-safe error handling
 */
export class WorkflowError extends Error {
  /**
   * Creates a new WorkflowError.
   *
   * @param message - Human-readable error message
   * @param cause - Optional underlying error that caused this error
   */
  constructor(message: string, public cause?: Error) {
    super(message);
    this.name = 'WorkflowError';
    Object.setPrototypeOf(this, WorkflowError.prototype);
  }
}

/**
 * Error thrown when a workflow definition is invalid.
 * Indicates problems with the workflow structure before execution.
 *
 * @remarks
 * Thrown by:
 * - Workflow builder validation
 * - DAG validation (validateAndSort)
 * - SDK wrapper before calling engine
 *
 * @example
 * ```typescript
 * // Duplicate step names
 * throw new WorkflowDefinitionError(
 *   'Duplicate step name "extract" found in workflow'
 * );
 *
 * // Missing required field
 * throw new WorkflowDefinitionError(
 *   'Step "summarize" is missing required field "model"'
 * );
 * ```
 */
export class WorkflowDefinitionError extends WorkflowError {
  constructor(message: string, cause?: Error) {
    super(message, cause);
    this.name = 'WorkflowDefinitionError';
    Object.setPrototypeOf(this, WorkflowDefinitionError.prototype);
  }
}

/**
 * Error thrown when there are dependency-related issues in the workflow graph.
 * A specialized subtype of WorkflowDefinitionError for DAG structure problems.
 *
 * @remarks
 * Thrown when:
 * - Circular dependencies are detected
 * - Dependencies reference non-existent steps
 * - Execution order cannot be determined
 *
 * @example
 * ```typescript
 * // Circular dependency
 * throw new WorkflowDependencyError(
 *   'Circular dependency detected: extract → enrich → extract'
 * );
 *
 * // Missing dependency
 * throw new WorkflowDependencyError(
 *   'Step "summarize" depends on unknown step "enrch"'
 * );
 * ```
 */
export class WorkflowDependencyError extends WorkflowDefinitionError {
  constructor(message: string, cause?: Error) {
    super(message, cause);
    this.name = 'WorkflowDependencyError';
    Object.setPrototypeOf(this, WorkflowDependencyError.prototype);
  }
}

/**
 * Error thrown when a step fails during execution.
 * Represents any fatal error during step execution that causes the workflow to stop.
 *
 * @remarks
 * Thrown by:
 * - Engine (executor.ts, runner.ts)
 * - SDK wrapping unexpected adapter/engine failures
 *
 * Contains:
 * - stepName: Which step failed
 * - workflowName: Which workflow was running
 * - attempts: How many retry attempts were made
 * - cause: Underlying error
 *
 * @example
 * ```typescript
 * throw new StepExecutionError(
 *   'extract',
 *   'invoice-processor',
 *   'Step "extract" failed after 3 attempts: ProviderError – OpenAI request timed out',
 *   3,
 *   providerError
 * );
 * ```
 */
export class StepExecutionError extends WorkflowError {
  /**
   * Creates a new StepExecutionError.
   *
   * @param stepName - Name of the step that failed
   * @param workflowName - Name of the workflow being executed
   * @param message - Human-readable error message
   * @param attempts - Number of execution attempts made
   * @param cause - Optional underlying error
   */
  constructor(
    public stepName: string,
    public workflowName: string,
    message: string,
    public attempts: number = 1,
    cause?: Error
  ) {
    super(message, cause);
    this.name = 'StepExecutionError';
    Object.setPrototypeOf(this, StepExecutionError.prototype);
  }
}

/**
 * Error thrown when a provider/adapter fails.
 * Represents provider-specific failures from model APIs.
 *
 * @remarks
 * **Important:** Adapters should NOT throw ProviderError directly.
 * Instead, they should return:
 * ```typescript
 * {
 *   success: false,
 *   error: {
 *     type: "ProviderError",
 *     message: "...",
 *     raw: originalProviderError
 *   }
 * }
 * ```
 *
 * The engine may wrap these into ProviderError instances for throwing.
 *
 * @example
 * ```typescript
 * // Missing API key
 * throw new ProviderError(
 *   'openai',
 *   'Missing API key for OpenAI provider',
 *   apiKeyError
 * );
 *
 * // Rate limit
 * throw new ProviderError(
 *   'openai',
 *   'Rate limit exceeded: 429 Too Many Requests',
 *   rateLimitError
 * );
 * ```
 */
export class ProviderError extends WorkflowError {
  /**
   * Creates a new ProviderError.
   *
   * @param provider - Name of the provider (e.g., "openai", "anthropic")
   * @param message - Human-readable error message
   * @param cause - Optional underlying provider error
   */
  constructor(public provider: string, message: string, cause?: Error) {
    super(message, cause);
    this.name = 'ProviderError';
    Object.setPrototypeOf(this, ProviderError.prototype);
  }
}

/**
 * Error thrown when a timeout occurs during execution.
 * Can occur at the step level or workflow level.
 *
 * @remarks
 * Timeouts can be configured:
 * - Per step: step.retry.timeout
 * - Per workflow: options.timeout
 *
 * @example
 * ```typescript
 * throw new TimeoutError(
 *   'Step "extract" timed out after 60000ms'
 * );
 *
 * throw new TimeoutError(
 *   'Workflow "invoice-processor" timed out after 300000ms'
 * );
 * ```
 */
export class TimeoutError extends WorkflowError {
  /**
   * Creates a new TimeoutError.
   *
   * @param message - Human-readable error message
   * @param timeoutMs - The timeout value that was exceeded
   * @param cause - Optional underlying error
   */
  constructor(message: string, public timeoutMs?: number, cause?: Error) {
    super(message, cause);
    this.name = 'TimeoutError';
    Object.setPrototypeOf(this, TimeoutError.prototype);
  }
}

/**
 * Error thrown when template interpolation fails.
 * Occurs when {{stepName.output}} references cannot be resolved.
 *
 * @remarks
 * Thrown when:
 * - Referenced step doesn't exist
 * - Referenced step hasn't executed yet
 * - Property path is invalid (e.g., {{extract.output.missing.field}})
 *
 * @example
 * ```typescript
 * throw new TemplateError(
 *   'Cannot resolve template variable "{{unknown.output}}": step "unknown" not found'
 * );
 *
 * throw new TemplateError(
 *   'Cannot access property "total" in "{{extract.output.total}}": output is undefined'
 * );
 * ```
 */
export class TemplateError extends WorkflowError {
  /**
   * Creates a new TemplateError.
   *
   * @param message - Human-readable error message
   * @param template - The template string that failed
   * @param cause - Optional underlying error
   */
  constructor(message: string, public template?: string, cause?: Error) {
    super(message, cause);
    this.name = 'TemplateError';
    Object.setPrototypeOf(this, TemplateError.prototype);
  }
}

/**
 * Type guard to check if an error is a WorkflowError.
 *
 * @param error - Error to check
 * @returns True if error is a WorkflowError
 */
export function isWorkflowError(error: unknown): error is WorkflowError {
  return error instanceof WorkflowError;
}

/**
 * Type guard to check if an error is a WorkflowDefinitionError.
 *
 * @param error - Error to check
 * @returns True if error is a WorkflowDefinitionError
 */
export function isWorkflowDefinitionError(error: unknown): error is WorkflowDefinitionError {
  return error instanceof WorkflowDefinitionError;
}

/**
 * Type guard to check if an error is a StepExecutionError.
 *
 * @param error - Error to check
 * @returns True if error is a StepExecutionError
 */
export function isStepExecutionError(error: unknown): error is StepExecutionError {
  return error instanceof StepExecutionError;
}

/**
 * Type guard to check if an error is a ProviderError.
 *
 * @param error - Error to check
 * @returns True if error is a ProviderError
 */
export function isProviderError(error: unknown): error is ProviderError {
  return error instanceof ProviderError;
}

/**
 * Type guard to check if an error is a TimeoutError.
 *
 * @param error - Error to check
 * @returns True if error is a TimeoutError
 */
export function isTimeoutError(error: unknown): error is TimeoutError {
  return error instanceof TimeoutError;
}
